// SB6-ANA-FILT-06 (D4/D5) — bridge between the L3 Kind option buttons'
// inner "advanced filter" icons / the Settings → Layout & Widgets
// checkboxes and the workspace grid.
//
// The two advanced widgets (AI Filter Module + Defect Filter Widget) are
// full workspace widgets, but unlike the five base widgets they start
// HIDDEN and are surfaced on demand:
//
//   * clicking the sliders icon on an L3 Kind button opens the matching
//     widget (and does NOT toggle the kind selection — D4), and
//   * the Layout & Widgets settings checkboxes show / hide them.
//
// The workspace owns the grid (`hidden` map + RGL `layouts`) in local
// React state, so it cannot be commanded imperatively from a click
// handler elsewhere in the tree. Instead this store carries:
//
//   * `visible` — the desired visibility of each advanced widget
//     (the single source of truth the workspace reconciles against), and
//   * `openTicket` — a monotonically increasing counter per widget that
//     the workspace watches so it can place the widget into the first
//     empty grid region on EACH explicit open (even if it was already
//     visible, an open re-focuses it; the workspace only repositions when
//     transitioning hidden→visible).
//
// Active state (D4 two-way sync with the L3 buttons) is handled by the
// existing data-point filter selection (`kind`), not here — this store is
// strictly about widget surfacing.
import { create } from "zustand";

export type AdvancedFilterWidgetKey = "aiFilter" | "defectFilter";

export const ADVANCED_FILTER_WIDGETS: readonly AdvancedFilterWidgetKey[] = [
  "aiFilter",
  "defectFilter",
];

interface AdvancedFilterWidgetState {
  /** Desired visibility of each advanced widget. */
  visible: Record<AdvancedFilterWidgetKey, boolean>;
  /** Bumped on every explicit `open` so the workspace can react. */
  openTicket: Record<AdvancedFilterWidgetKey, number>;
  /** Open (show + request placement into the first empty region). */
  open: (key: AdvancedFilterWidgetKey) => void;
  /** Hide the widget. */
  close: (key: AdvancedFilterWidgetKey) => void;
  /** Settings checkbox: set visibility directly (open when turning on). */
  setVisible: (key: AdvancedFilterWidgetKey, visible: boolean) => void;
}

export const useAdvancedFilterWidgetStore = create<AdvancedFilterWidgetState>(
  (set) => ({
    visible: { aiFilter: false, defectFilter: false },
    openTicket: { aiFilter: 0, defectFilter: 0 },
    open: (key) => {
      set((s) => ({
        visible: { ...s.visible, [key]: true },
        openTicket: { ...s.openTicket, [key]: s.openTicket[key] + 1 },
      }));
    },
    close: (key) => {
      set((s) => ({ visible: { ...s.visible, [key]: false } }));
    },
    setVisible: (key, visible) => {
      set((s) => ({
        visible: { ...s.visible, [key]: visible },
        openTicket: visible
          ? { ...s.openTicket, [key]: s.openTicket[key] + 1 }
          : s.openTicket,
      }));
    },
  }),
);
